import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { formatDistanceToNow, type Locale } from "date-fns"
import { de } from "date-fns/locale"
import { Bell } from "lucide-react"
import { Component, ReactNode } from "react"
import { useNavigate } from "react-router"
import { useMarkActed } from "../hooks/useMarkActed"
import { useMarkRead } from "../hooks/useMarkRead"
import { resolve } from "../registry/registry"
import type {
  ActionContext,
  NotificationAccent,
  NotificationDTO,
} from "../types"

export type NotificationItemVariant = "popover" | "page"

interface NotificationItemProps {
  notification: NotificationDTO
  variant?: NotificationItemVariant
  /** Popover only: close the popover before navigating away. */
  onNavigateAway?: () => void
  /** Page variant: primary emphasis for the CTA button. */
  ctaEmphasis?: "primary" | "secondary"
  /** Page variant: show the "Mark as done" (markActed only, no navigation) ghost button. */
  showDismiss?: boolean
}

const ACCENT_CHIP_CLASSES: Record<NotificationAccent, string> = {
  default: "bg-white/6 text-muted-foreground",
  warning: "bg-orange-500/[0.13] text-orange-400",
  error: "bg-destructive/13 text-destructive",
}

// Page rows match /history's row chrome (QuestionnaireListItem): px-S py-M
// spacing, yellow1/10 hover, no rounding. Popover stays dense with its own look.
const rowChromeClass = (variant: NotificationItemVariant): string =>
  variant === "popover"
    ? "rounded-[10px] py-2 pl-[18px] pr-4 hover:bg-white/[0.035]"
    : "px-S py-M hover:bg-yellow1/10"

const chipSizeClass = (variant: NotificationItemVariant): string =>
  variant === "popover" ? "h-8 w-8" : "h-9 w-9"

const getRelativeTime = (
  createdAt: Date | string,
  locale?: Locale,
): string => {
  const date = createdAt instanceof Date ? createdAt : new Date(createdAt)
  if (isNaN(date.getTime())) {
    return ""
  }
  return formatDistanceToNow(date, { addSuffix: true, locale })
}

const NotificationItemContent: React.FC<NotificationItemProps> = ({
  notification,
  variant = "popover",
  onNavigateAway,
  ctaEmphasis = "secondary",
  showDismiss = false,
}) => {
  const navigate = useNavigate()
  const { translate, currentLanguage } = useTranslation()
  const dateFnsLocale = currentLanguage === "DE" ? de : undefined
  const { mutate: markRead } = useMarkRead()
  const { mutate: markActed } = useMarkActed()

  const renderer = resolve(notification.type)
  const payload = notification.payload ?? {}

  const isUnread = notification.readAt === null
  const isActed = notification.actedAt !== null
  /** "New since last visit" = servedAt === null at fetch time (snapshot). */
  const isNew = notification.servedAt === null
  const isActionRequired = notification.kind === "action_required"

  const titleText = renderer.title(payload)
  const title = translate(titleText.key, titleText.params)
  const descriptionText = renderer.description?.(payload)
  const description = descriptionText
    ? translate(descriptionText.key, descriptionText.params)
    : undefined
  const ctaLabel = renderer.cta ? translate(renderer.cta) : undefined
  const Icon = renderer.icon

  const handleMarkRead = () => {
    if (isUnread) {
      markRead(notification.id)
    }
  }

  const ctx: ActionContext = {
    navigate,
    markRead: handleMarkRead,
    closePopover: () => onNavigateAway?.(),
  }

  // Whether this type ever has something to navigate to. MANDATORY per spec:
  // action_required rows always render their CTA button; info rows with an
  // action (e.g. questionnaire_ready) get a subtle CTA too. Unknown types
  // (FALLBACK has no cta) never show a button.
  const hasNavigableAction =
    renderer.cta !== undefined &&
    (isActionRequired || notification.action !== null)

  // Button only shows pre-acted (once handled, it collapses away). The row
  // itself keeps acting as the CTA even after acting/reading — so an already
  // "done" notification stays clickable to reopen its resource, same as
  // reopening a read email.
  const showCta = !isActed && hasNavigableAction
  const rowActsAsCta = hasNavigableAction

  const handleRowClick = () => {
    if (rowActsAsCta) {
      if (!isActed) markActed(notification.id)
      renderer.onAction(notification.action, ctx)
      return
    }
    handleMarkRead()
  }

  // Explicit button click does the same as a row click; stopPropagation just
  // avoids double-firing the row's own onClick.
  const handleCtaClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    markActed(notification.id)
    renderer.onAction(notification.action, ctx)
  }

  const handleMarkDone = (event: React.MouseEvent) => {
    event.stopPropagation()
    markActed(notification.id)
  }

  // Read/acted rows stay in the list at reduced opacity — never collapsed
  // into a different layout — so nothing silently disappears.
  const isSettled = !isUnread || isActed

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`notification-item-${notification.id}`}
      onClick={handleRowClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          handleRowClick()
        }
      }}
      className={cn(
        "group relative flex w-full cursor-pointer gap-3 text-left transition-colors",
        // Popover rows are single-line (icon, title, date) — center them.
        // Page rows carry description/CTA below the title, so the icon stays
        // top-aligned against that taller block.
        variant === "popover" ? "items-center" : "items-start",
        rowChromeClass(variant),
        isNew && "bg-primary/5",
        isSettled && "opacity-60",
      )}
    >
      {isNew ? (
        <span
          data-testid={`notification-new-bar-${notification.id}`}
          aria-hidden="true"
          className="absolute bottom-2 left-0 top-2 w-[2px] rounded-full bg-primary/55"
        />
      ) : null}

      <span className="relative shrink-0">
        <span
          className={cn(
            "flex items-center justify-center rounded-[10px]",
            chipSizeClass(variant),
            ACCENT_CHIP_CLASSES[renderer.accent ?? "default"],
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        {isUnread ? (
          <span
            data-testid={`notification-unread-dot-${notification.id}`}
            className="absolute -left-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background"
            aria-label={translate("NOTIF/UI/UNREAD")}
          />
        ) : null}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Typography
          as="span"
          variant="textS"
          className={cn(isUnread ? "text-foreground" : "text-muted-foreground")}
        >
          {title}
        </Typography>
        {description && variant === "page" ? (
          <Typography as="span" variant="textXS" className="text-muted-foreground">
            {description}
          </Typography>
        ) : null}
        {variant === "page" ? (
          <Typography
            as="span"
            variant="textXS"
            className="tabular-nums text-muted-foreground"
          >
            {getRelativeTime(notification.createdAt, dateFnsLocale)}
          </Typography>
        ) : null}
      </span>

      {variant === "page" ? (
        showCta ? (
          <span className="flex shrink-0 items-center gap-2 self-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <Button
              type="button"
              variant={ctaEmphasis === "primary" ? "default" : "secondary"}
              size="sm"
              className="h-7 px-2.5 text-xs"
              data-testid={`notification-cta-${notification.id}`}
              onClick={handleCtaClick}
            >
              {ctaLabel}
            </Button>
            {showDismiss ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2.5 text-xs text-muted-foreground"
                data-testid={`notification-mark-done-${notification.id}`}
                onClick={handleMarkDone}
              >
                {translate("NOTIF/UI/MARK_AS_DONE")}
              </Button>
            ) : null}
          </span>
        ) : null
      ) : (
        <span className="flex shrink-0 flex-col items-end gap-1.5">
          <Typography
            as="span"
            variant="textXS"
            className="tabular-nums text-muted-foreground"
          >
            {getRelativeTime(notification.createdAt, dateFnsLocale)}
          </Typography>
        </span>
      )}
    </div>
  )
}

interface ErrorBoundaryProps {
  notificationId: string
  fallbackText: string
  variant: NotificationItemVariant
  children: ReactNode
}

/**
 * Per-item error boundary: one bad payload renders a fallback row instead of
 * killing the whole list.
 */
class NotificationItemErrorBoundary extends Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      const { variant } = this.props
      return (
        <div
          data-testid={`notification-item-error-${this.props.notificationId}`}
          className={cn("flex w-full items-center gap-3", rowChromeClass(variant))}
        >
          <span
            className={cn(
              "flex shrink-0 items-center justify-center rounded-[10px] bg-white/6 text-muted-foreground",
              chipSizeClass(variant),
            )}
          >
            <Bell className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="text-sm text-muted-foreground">
            {this.props.fallbackText}
          </span>
        </div>
      )
    }
    return this.props.children
  }
}

export const NotificationItem: React.FC<NotificationItemProps> = (props) => {
  const { translate } = useTranslation()
  return (
    <NotificationItemErrorBoundary
      notificationId={props.notification.id}
      fallbackText={translate("NOTIF/UI/ITEM_ERROR")}
      variant={props.variant ?? "popover"}
    >
      <NotificationItemContent {...props} />
    </NotificationItemErrorBoundary>
  )
}
